/**
 * Mounts the local developer role-simulation lane (#567) around the session.
 *
 * Rendered by `AppRuntime` between the query client and the session gate, and
 * a NO-OP unless `localAccessConfig` answers — which it does only in the
 * `local` environment with the `local.roleSimulation` switch on. Cloud
 * bundles render children directly with nothing added to the tree.
 *
 * When active it does two things, in order:
 *
 * 1. ENSURE the developer's chosen profile before the first session query.
 *    The controller call is idempotent (an unchanged profile writes nothing),
 *    so a fresh database gets the browser-local default — Admin — while a
 *    later boot re-asserts whatever the developer last chose. This is what
 *    makes a fresh `task k8s:up` + Entra login land in a usable editor with
 *    no bootstrap command.
 * 2. Render the conspicuous banner with the effective profile and the
 *    self-profile controls, above everything else on every screen.
 *
 * A controller failure is NOT a fallback to anything: the lane degrades to
 * exactly the strict behavior (whatever `authz_role_assignments` already
 * holds), and the banner says so. Nothing here touches what the Editor
 * Service resolves — the banner's roles are the controller's answer, and the
 * capabilities the app obeys still come from `/v1/session` on every request.
 */

import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { AuthAdapter } from "@/auth/types";
import type { RuntimeConfig } from "@/config/schema";

import { LocalDevBanner } from "./LocalDevBanner";
import {
  fetchSelfProfile,
  localAccessConfig,
  putSelfProfile,
  storeDesiredRoles,
  storedDesiredRoles,
  type LocalAccessConfig,
  type LocalProfile,
  type LocalRole,
} from "./localAccess";

export interface LocalDevContextValue {
  profile: LocalProfile | null;
  /** Human-readable controller failure, when the last call refused. */
  error: string | null;
  applying: boolean;
  /** Replace the signed-in developer's own local roles, then refetch everything. */
  apply: (roles: LocalRole[]) => Promise<void>;
}

const LocalDevContext = createContext<LocalDevContextValue | null>(null);

/** Null outside the active local role-simulation lane. */
export function useLocalDev(): LocalDevContextValue | null {
  return useContext(LocalDevContext);
}

export function LocalDevProvider({
  config,
  adapter,
  children,
}: {
  config: RuntimeConfig;
  adapter: AuthAdapter;
  children: ReactNode;
}) {
  const access = localAccessConfig(config);
  if (!access) {
    return children;
  }
  return (
    <LocalDevRuntime access={access} adapter={adapter}>
      {children}
    </LocalDevRuntime>
  );
}

function LocalDevRuntime({
  access,
  adapter,
  children,
}: {
  access: LocalAccessConfig;
  adapter: AuthAdapter;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [ensured, setEnsured] = useState(false);
  const [profile, setProfile] = useState<LocalProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const applyInFlightRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    async function ensure() {
      try {
        const applied = await putSelfProfile(access, adapter, storedDesiredRoles());
        if (cancelled) return;
        setProfile(applied);
        setError(null);
      } catch (cause) {
        // The write path refused; the read may still answer (e.g. a 503 from
        // the fail-before-write guard). Either way the lane continues in
        // strict behavior — stored assignments only — and the banner names it.
        let fallback: LocalProfile | null = null;
        try {
          fallback = await fetchSelfProfile(access, adapter);
        } catch {
          fallback = null;
        }
        if (cancelled) return;
        setProfile(fallback);
        setError(cause instanceof Error ? cause.message : "The local access controller failed.");
      } finally {
        if (!cancelled) setEnsured(true);
      }
    }
    void ensure();
    return () => {
      cancelled = true;
    };
  }, [access, adapter]);

  const apply = useCallback(
    async (roles: LocalRole[]) => {
      if (applyInFlightRef.current) return;
      applyInFlightRef.current = true;
      setApplying(true);
      try {
        const applied = await putSelfProfile(access, adapter, roles);
        storeDesiredRoles(roles);
        setProfile(applied);
        setError(null);
        // Authority changed for EVERY query — the session, the project list,
        // the targets, all of it re-resolves against the new assignments.
        await queryClient.invalidateQueries();
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "The local access controller failed.");
        throw cause;
      } finally {
        applyInFlightRef.current = false;
        setApplying(false);
      }
    },
    [access, adapter, queryClient],
  );

  const value = useMemo<LocalDevContextValue>(
    () => ({ profile, error, applying, apply }),
    [profile, error, applying, apply],
  );

  if (!ensured) {
    // The ensure call runs BEFORE the session query by construction: children
    // (the session gate) do not mount until it settles, success or refusal.
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center bg-background"
      >
        <p className="text-sm text-muted-foreground">Preparing your local developer profile…</p>
      </div>
    );
  }

  return (
    <LocalDevContext.Provider value={value}>
      <div className="flex min-h-screen flex-col">
        <LocalDevBanner />
        <div className="min-h-0 flex-1">{children}</div>
      </div>
    </LocalDevContext.Provider>
  );
}
